import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Handles tag filtering for calendar events
export default class extends Controller {
  static targets = ["button", "dropdown", "tagList", "label", "badge"]
  static values = {
    tagsUrl: String,
    selected: { type: Array, default: [] }
  }

  connect() {
    this.tags = []
    this.tagsLoaded = false
    this.loadTags()
    this.applyDebounceTimeout = null

    // Close dropdown when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.boundCloseOnClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnClickOutside)
    if (this.applyDebounceTimeout) {
      clearTimeout(this.applyDebounceTimeout)
    }
  }

  async loadTags() {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    try {
      const response = await fetch(this.tagsUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        this.renderError("Failed to load ministries")
        return
      }

      this.tags = await response.json()
      this.tagsLoaded = true
      this.renderTags()
      this.updateLabel()
    } catch (error) {
      clearTimeout(timeoutId)
      if (error.name === "AbortError") {
        this.renderError("Request timed out", true)
      } else {
        console.error("Failed to load tags:", error)
        this.renderError("Failed to load ministries", true)
      }
    }
  }

  renderError(message, showRetry = false) {
    if (!this.hasTagListTarget) return

    const retryButton = showRetry
      ? `<button type="button" data-action="click->filter#retry" class="ml-2 text-accent-600 hover:text-accent-800 underline">Retry</button>`
      : ""

    this.tagListTarget.innerHTML = `
      <div class="px-4 py-2 text-sm text-red-600">${this.escapeHtml(message)}${retryButton}</div>
    `
  }

  retry() {
    if (!this.hasTagListTarget) return

    this.tagListTarget.innerHTML = `
      <div class="px-4 py-2 text-sm text-gray-500">Loading...</div>
    `
    this.loadTags()
  }

  renderTags() {
    if (!this.hasTagListTarget) return

    if (this.tags.length === 0) {
      this.tagListTarget.innerHTML = `
        <div class="px-4 py-2 text-sm text-gray-500">No ministries available</div>
      `
      return
    }

    // Clear existing content and build DOM elements safely
    this.tagListTarget.innerHTML = ""

    this.tags.forEach(tag => {
      const label = document.createElement("label")
      label.className = "flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"

      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.value = tag.id
      checkbox.checked = this.selectedValue.includes(tag.id)
      checkbox.dataset.action = "change->filter#toggleTag"
      checkbox.className = "h-4 w-4 rounded border-gray-300 text-accent-600 focus:ring-accent-500"

      const span = document.createElement("span")
      span.className = "ml-3 flex items-center gap-2 text-sm text-gray-700"

      // Add color indicator if valid hex color
      if (tag.color && this.isValidHexColor(tag.color)) {
        const colorDot = document.createElement("span")
        colorDot.className = "inline-block w-2 h-2 rounded-full"
        colorDot.style.backgroundColor = tag.color
        span.appendChild(colorDot)
      }

      // Safely add tag name as text (prevents XSS)
      span.appendChild(document.createTextNode(tag.name))

      label.appendChild(checkbox)
      label.appendChild(span)
      this.tagListTarget.appendChild(label)
    })
  }

  // Validate hex color format to prevent CSS injection (supports #RGB and #RRGGBB)
  isValidHexColor(color) {
    return /^#([0-9A-Fa-f]{3}){1,2}$/.test(color)
  }

  // Escape HTML to prevent XSS in error messages
  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  toggle(event) {
    event.stopPropagation()
    this.dropdownTarget.classList.toggle("hidden")
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  toggleTag(event) {
    const tagId = event.target.value

    if (event.target.checked) {
      if (!this.selectedValue.includes(tagId)) {
        this.selectedValue = [...this.selectedValue, tagId]
      }
    } else {
      this.selectedValue = this.selectedValue.filter(id => id !== tagId)
    }

    this.updateLabel()
    this.debouncedApplyFilter()
  }

  clearAll(event) {
    // Prevent default but don't stop propagation to allow proper event flow
    if (event) {
      event.preventDefault()
    }

    // Clear all selections
    this.selectedValue = []

    // Update UI before navigation
    if (this.tags && this.tags.length > 0) {
      this.renderTags()
    }
    this.updateLabel()

    // Navigate to clear filters from URL
    this.applyFilter()
  }

  updateLabel() {
    if (!this.hasLabelTarget) return
    // Preserve server-rendered label until tags are loaded
    if (!this.tagsLoaded) return

    const count = this.selectedValue.length
    if (count === 0) {
      this.labelTarget.textContent = "Filter by ministry"
    } else if (count === 1) {
      const tag = this.tags.find(t => t.id === this.selectedValue[0])
      this.labelTarget.textContent = tag ? tag.name : "1 filter"
    } else {
      this.labelTarget.textContent = `${count} filters`
    }
    this.updateBadge()
  }

  updateBadge() {
    if (!this.hasBadgeTarget) return

    const count = this.selectedValue.length
    if (count > 0) {
      this.badgeTarget.textContent = count
      this.badgeTarget.classList.remove("hidden")
    } else {
      this.badgeTarget.classList.add("hidden")
    }
  }

  // Debounce filter application to avoid rapid navigation
  debouncedApplyFilter() {
    if (this.applyDebounceTimeout) {
      clearTimeout(this.applyDebounceTimeout)
    }
    this.applyDebounceTimeout = setTimeout(() => this.applyFilter(), 300)
  }

  applyFilter() {
    const url = new URL(window.location.href)

    // Clear existing tag_ids
    url.searchParams.delete("tag_ids[]")

    // Add selected tag_ids
    this.selectedValue.forEach(id => {
      url.searchParams.append("tag_ids[]", id)
    })

    // Navigate with Turbo
    Turbo.visit(url.toString())
  }

  // Only update checkboxes, not full re-render (avoids unnecessary DOM churn)
  selectedValueChanged() {
    if (!this.hasTagListTarget) return

    const checkboxes = this.tagListTarget.querySelectorAll("input[type='checkbox']")
    checkboxes.forEach(checkbox => {
      checkbox.checked = this.selectedValue.includes(checkbox.value)
    })
    this.updateLabel()
  }
}
